import {
  ReactNode,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useUser } from "@/providers/use-user";
import { getConversationsForUser } from "@/services/conversation-service";
import { getIdentifierConfig } from "@/lib/identifier-config";
import { ThreadContext } from "./thread-context";
import type { Thread } from "@/features/chat/types/sdk";
import { CURRENT_CONVERSATION_IDENTIFIER } from "@/constants";

interface ThreadFetchOptions {
  limit?: number;
  metadata?: Record<string, unknown>;
}

export function ThreadProvider({ children }: { children: ReactNode }) {
  const { userId, isLoading: userLoading } = useUser();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);

  const getThreads = useCallback(
    async (options?: ThreadFetchOptions): Promise<Thread[]> => {
      if (!userId) {
        console.warn("Cannot fetch threads: userId is not set");
        return [];
      }

      // Fetch conversations from Firebase Firestore
      const config = getIdentifierConfig();
      const conversations = await getConversationsForUser(userId);

      const nameField = config.fieldName.replace('_id', '_name');

      // Transform conversations to SDK Thread format
      const threads: Thread[] = conversations.map((conversation) => ({
        thread_id: conversation[config.fieldName],
        created_at: conversation.created_at,
        updated_at: conversation.updated_at,
        metadata: {
          project_name: conversation[nameField],
          address: conversation.address,
          address_lat: conversation.address_lat,
          address_lng: conversation.address_lng,
          address_place_id: conversation.address_place_id,
        },
      }));

      return threads;
    },
    [userId],
  );

  // Auto-fetch threads when userId becomes available
  useEffect(() => {
    if (!userLoading && userId && threads.length === 0) {
      setThreadsLoading(true);
      getThreads()
        .then((fetchedThreads) => {
          setThreads(fetchedThreads);

          // Validate that current threadId in sessionStorage belongs to this user
          // This prevents security issues where a different user could see another user's thread
          const currentThreadId = sessionStorage.getItem(CURRENT_CONVERSATION_IDENTIFIER);
          if (currentThreadId) {
            const threadBelongsToUser = fetchedThreads.some(
              (t) => t.thread_id === currentThreadId
            );
            if (!threadBelongsToUser) {
              console.warn(
                "⚠️ Clearing threadId from sessionStorage - does not belong to current user"
              );
              sessionStorage.removeItem(CURRENT_CONVERSATION_IDENTIFIER);
            }
          }
        })
        .catch((error) => {
          console.error("❌ Error auto-fetching threads:", error);
        })
        .finally(() => {
          setThreadsLoading(false);
        });
    }
  }, [userId, userLoading, getThreads, threads.length]);

  const value = {
    getThreads,
    threads,
    setThreads,
    threadsLoading,
    setThreadsLoading,
  };

  return (
    <ThreadContext.Provider value={value}>{children}</ThreadContext.Provider>
  );
}
